import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useEffect } from 'react';
import ReactGA4 from 'react-ga4';

import styles from './index.module.scss';
import { useRecommendUsers } from './hook';
import { UnionAvatar, UnionFollowButton, UserCardPopup, UserName } from '@/components';
import { formatNetworth, formatNum, openNewWindow } from '@/utils';
import { isEmpty } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { useUserType } from '@/hooks';

export const RecommendUsers = () => {
  const { t } = useTranslation();
  const userType = useUserType();
  const { data, isLoading } = useRecommendUsers();

  const handleClick = useCallback(
    (id: string) => {
      ReactGA4.event({
        category: 'Stream Follow Guide',
        action: `Enter_Stream_Whales_${userType}`,
      });
      openNewWindow(`/profile/${id}/stream`, '_blank');
    },
    [userType],
  );

  useEffect(() => {
    ReactGA4.event({
      category: 'Stream Follow Guide',
      action: `Show_Stream_Whales_${userType}`,
    });
  }, [userType]);

  const handleFollow = useCallback(
    isSuccess => {
      ReactGA4.event({
        category: 'Stream Follow Guide',
        action: `Follow_Stream_Whales_${userType}_${isSuccess ? 'Success' : 'Fail'}`,
      });
    },
    [userType],
  );

  if (!isLoading && isEmpty(data)) {
    return null;
  }
  return (
    <div className={styles.container}>
      <div className={styles.title}>{t('RecommendUser.14m0goq')}</div>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className={styles.userList}>
          {data?.map(user => (
            <div className={styles.item} onClick={() => handleClick(user.id)} key={user?.id}>
              <div className={styles.avatar}>
                <UnionAvatar data={user} size={48} userCardPopup />
              </div>
              <div className={styles.info}>
                <div className={styles.top}>
                  <div className={styles.name}>
                    <UserCardPopup
                      address={user.id}
                      restParams={{
                        source: 'recommend',
                      }}>
                      <UserName
                        address={user.id}
                        name={user?.desc?.user?.web3_id}
                        userDesc={user?.desc}
                      />
                    </UserCardPopup>
                  </div>
                  <div className={styles.netWorth}>
                    {t('RecommendUser.mshpv5')}
                    <span>{formatNetworth(user?.desc?.usd_value)}</span>
                  </div>
                </div>
                <div className={styles.articleCount}>
                  {t('RecommendUser.v55a0f', { amount: formatNum(user?.article_count) })}
                </div>
              </div>
              {user?.id ? (
                <UnionFollowButton
                  type="primary"
                  followingType="default"
                  className={styles.followBtn}
                  address={user?.id}
                  isFollowing={user?.is_following}
                  onFollowCallback={handleFollow}
                  restParams={{
                    source: 'recommend',
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const RecommendUserItemSkeleton = memo(() => {
  return (
    <div className={styles.item}>
      <Skeleton width={38} height={38} circle className={styles.avatar} />
      <div className={styles.info}>
        <div className={styles.top}>
          <div className={styles.name}>
            <Skeleton width={70} />
          </div>
        </div>
        <Skeleton className={styles.articleCount} width={180} height={15} />
      </div>
    </div>
  );
});

export const LoadingSkeleton = memo(() => (
  <div className={styles.userList}>
    {Array(10)
      .fill(0)
      .map((_, index) => (
        <RecommendUserItemSkeleton key={index} />
      ))}
  </div>
));
